import { useMemo } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezonePlugin from 'dayjs/plugin/timezone'
import { useTimezone } from 'common/hooks/useTimezone'
import type { PatientEncounter } from 'features/Procedure/state/procedure-api.type'

dayjs.extend(utc)
dayjs.extend(timezonePlugin)

export const STALE_ENCOUNTER_HOURS = 72

// Pure function — testable without a React renderer.
export const isEncounterStaleNow = (
  selectedEncounterUid: string | null,
  encounters: PatientEncounter[],
  timeZone: string | undefined,
  now: dayjs.Dayjs = dayjs(),
): boolean => {
  if (!timeZone) return false
  if (!selectedEncounterUid) return false

  const encounter = encounters.find((e) => e.encounter_uuid === selectedEncounterUid)
  if (!encounter || !encounter.encounter_date) return false

  let encounterInstant: dayjs.Dayjs
  try {
    encounterInstant = dayjs.tz(encounter.encounter_date, timeZone)
  } catch {
    return false
  }
  if (!encounterInstant.isValid()) return false

  const nowInstant = now.tz(timeZone)
  const hoursElapsed = nowInstant.diff(encounterInstant, 'hour', true)

  return hoursElapsed > STALE_ENCOUNTER_HOURS
}

export const useEncounterStaleLock = (
  selectedEncounterUid: string | null,
  encounters: PatientEncounter[],
): boolean => {
  const { timeZone } = useTimezone()

  return useMemo(
    () => isEncounterStaleNow(selectedEncounterUid, encounters, timeZone),
    [selectedEncounterUid, encounters, timeZone],
  )
}
